import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { DashboardBadges } from "../components/credit/CreditBadges";
import PayoutsPanel from "../components/PayoutsPanel";
import BookingChat from "../components/BookingChat";
import DisputeDialog from "../components/DisputeDialog";
import PromoDialog from "../components/PromoDialog";
import ManualRecommendations from "../components/ManualRecommendations";
import AutoCareCta from "../components/AutoCareCta";
import MaintenanceCard from "../components/MaintenanceCard";
import AcademyCta from "../components/AcademyCta";
import FleetPromoDialog from "../components/FleetPromoDialog";
import MyDisputes from "../components/MyDisputes";
import MySales from "../components/MySales";
import SellerStats from "../components/SellerStats";
import SavedSearches from "../components/SavedSearches";
import Watchlist from "../components/Watchlist";
import DriverCardPanel from "../components/DriverCard";
import DriverStreak from "../components/DriverStreak";
import ChampionBadge from "../components/ChampionBadge";
import RoadReadyBadge from "../components/RoadReadyBadge";
import NewDeviceBanner from "../components/NewDeviceBanner";
import DeviceManager from "../components/DeviceManager";
import { replayTour } from "../components/WelcomeTour";
import GettingStarted from "../components/GettingStarted";
import NewHireDocs from "../components/NewHireDocs";
import CreditExpiryCard from "../components/CreditExpiryCard";
import GiftThanksCard from "../components/GiftThanksCard";
import ThanksRepliesCard from "../components/ThanksRepliesCard";
import BuyGiftCard from "../components/BuyGiftCard";
import { Car, DollarSign, TrendingUp, Clock, Plus, ArrowRight, MessageSquare, Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function Stat({ icon: I, label, value, accent }) {
  return (
    <div className="p-5 rounded-2xl bg-[#131722] border border-slate-800 hover:border-slate-700 transition-all">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accent || "bg-slate-900"}`}><I className="w-4 h-4" /></div>
      <p className="text-xs font-mono uppercase tracking-widest text-slate-400">{label}</p>
      <p className="font-display font-extrabold text-2xl mt-1">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [hireKey, setHireKey] = useState(0);
  const [owner, setOwner] = useState(null);
  const [renter, setRenter] = useState(null);
  const [myCars, setMyCars] = useState([]);
  const [chatBooking, setChatBooking] = useState(null);
  const [disputeKey, setDisputeKey] = useState(0);

  const loadAll = useCallback(async () => {
    const [o, r, m] = await Promise.allSettled([
      api.get("/dashboard/owner"),
      api.get("/dashboard/renter"),
      api.get("/my/cars"),
    ]);
    setOwner(o.status === "fulfilled" ? o.value.data : null);
    setRenter(r.status === "fulfilled" ? r.value.data : null);
    setMyCars(m.status === "fulfilled" ? m.value.data : []);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleDynamic = async (car) => {
    try {
      const next = !car.dynamic_pricing;
      await api.post(`/cars/${car.car_id}/dynamic-pricing`, { enabled: next });
      toast.success(next ? "AI will re-price nightly" : "Dynamic pricing paused");
      setMyCars((cs) => cs.map((c) => c.car_id === car.car_id ? { ...c, dynamic_pricing: next } : c));
    } catch { toast.error("Failed to toggle"); }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="dashboard-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4 rise">
        <div>
          <p className="font-mono text-xs tracking-widest uppercase text-cyan-400 mb-1">// Welcome back</p>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">{user?.name}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.account_type && <Button variant="outline" onClick={replayTour} className="border-slate-700 text-slate-300" data-testid="dash-replay-tour"><Sparkles className="w-4 h-4 mr-2" />Replay tour</Button>}
          <BuyGiftCard variant="button" />
          <Link to="/credit"><Button variant="outline" className="border-sky-700 text-sky-300 hover:bg-sky-950" data-testid="dash-credit-btn"><ShieldCheck className="w-4 h-4 mr-2" />Credit Empowerment</Button></Link>
          <Link to="/list-your-car"><Button className="bg-white text-slate-950 hover:bg-slate-100 font-semibold" data-testid="dash-list-btn"><Plus className="w-4 h-4 mr-2" />List a car</Button></Link>
        </div>
      </div>

      <NewDeviceBanner />
      <DashboardBadges />
      <GettingStarted onReward={refresh} key={hireKey} />
      <NewHireDocs onChange={() => setHireKey((k) => k + 1)} />
      <GiftThanksCard />
      <ThanksRepliesCard />
      <CreditExpiryCard />
      <ManualRecommendations />
      <div className="mb-8 grid lg:grid-cols-2 gap-4"><AutoCareCta context="between trips" /><MaintenanceCard cars={myCars} /></div>
      <div className="mb-8"><AcademyCta /></div>

      <Tabs defaultValue="owner" className="rise rise-1">
        <TabsList className="bg-slate-900/60 border border-slate-800">
          <TabsTrigger value="owner" data-testid="tab-owner">As Host</TabsTrigger>
          <TabsTrigger value="renter" data-testid="tab-renter">As Driver</TabsTrigger>
        </TabsList>

        <TabsContent value="owner" className="mt-6 space-y-6" data-testid="owner-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={DollarSign} label="Total earnings" value={`$${(owner?.total_earnings ?? 0).toFixed(2)}`} accent="bg-emerald-500/20 text-emerald-400" />
            <Stat icon={Car} label="Cars listed" value={owner?.cars_count ?? 0} accent="bg-blue-500/20 text-blue-400" />
            <Stat icon={Clock} label="Active trips" value={owner?.active_trips ?? 0} accent="bg-cyan-500/20 text-cyan-400" />
            <Stat icon={TrendingUp} label="Completed" value={owner?.completed_trips ?? 0} accent="bg-slate-800 text-slate-200" />
          </div>

          <PayoutsPanel />

          <div>
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h2 className="font-display text-xl font-bold">Your fleet</h2>
              {myCars.length > 0 && <FleetPromoDialog carCount={myCars.length} onSaved={(promo) => setMyCars((cs) => cs.map((x) => ({ ...x, promo })))} />}
            </div>
            {myCars.length === 0 ? (
              <div className="p-8 rounded-2xl border border-dashed border-slate-800 text-center">
                <p className="text-slate-400 mb-4">No cars yet. List your first car in under 2 minutes.</p>
                <Link to="/list-your-car"><Button className="bg-white text-slate-950 hover:bg-slate-100 font-semibold" data-testid="empty-list-btn">List a car <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myCars.map((c) => (
                  <div key={c.car_id} data-testid={`my-car-${c.car_id}`} className="group p-4 rounded-2xl bg-[#131722] border border-slate-800 hover:border-blue-500/50 transition-all">
                    <Link to={`/cars/${c.car_id}`} className="flex gap-4">
                      <div className="w-20 h-20 shrink-0 rounded-xl overflow-hidden bg-slate-900">
                        {c.photos?.[0] && <img src={c.photos[0]} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-bold truncate">{c.year} {c.make} {c.model}</p>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">{c.city}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="font-mono text-sm text-emerald-400">${c.daily_price}/d</span>
                          {c.gig_ready && <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px]">GIG</Badge>}
                        </div>
                      </div>
                    </Link>
                    <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs flex items-center gap-1.5 text-slate-300"><Sparkles className="w-3 h-3 text-cyan-400" />AI dynamic pricing</span>
                      <Switch checked={!!c.dynamic_pricing} onCheckedChange={() => toggleDynamic(c)} data-testid={`dynamic-toggle-${c.car_id}`} />
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-800 flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-500">{c.promo?.enabled ? `-${c.promo.weekend_discount_pct}% weekend` : "No promo"}</span>
                      <PromoDialog car={c} onSaved={(promo) => setMyCars((cs) => cs.map((x) => x.car_id === c.car_id ? { ...x, promo } : x))} />
                    </div>
                    {c.dynamic_pricing && c.last_repriced_at && (
                      <p className="text-[10px] text-slate-500 font-mono mt-1">Last re-priced {new Date(c.last_repriced_at).toLocaleDateString()}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-display text-xl font-bold mb-3">Recent paid bookings</h2>
            {(owner?.recent_bookings || []).length === 0 ? (
              <p className="text-sm text-slate-500">No bookings yet.</p>
            ) : (
              <div className="space-y-2">
                {owner.recent_bookings.map((b) => (
                  <div key={b.booking_id} className="flex items-center justify-between p-4 rounded-xl bg-[#131722] border border-slate-800" data-testid={`owner-booking-${b.booking_id}`}>
                    <div>
                      <p className="font-semibold">{b.car_label}</p>
                      <p className="text-xs text-slate-400">{b.start_date} → {b.end_date} · {b.renter_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setChatBooking({ id: b.booking_id, label: b.car_label })} data-testid={`owner-chat-btn-${b.booking_id}`} className="bg-transparent border-slate-700 hover:bg-slate-800">
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Chat
                      </Button>
                      <DisputeDialog bookingId={b.booking_id} onCreated={() => setDisputeKey((k) => k + 1)} trigger={
                        <Button variant="outline" size="sm" data-testid={`owner-dispute-btn-${b.booking_id}`} className="bg-transparent border-slate-700 hover:bg-slate-800 text-amber-300">
                          <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />Dispute
                        </Button>} />
                      <p className="font-mono text-emerald-400">${b.subtotal.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <MyDisputes refreshKey={disputeKey} />
          <MySales />
          <SellerStats />
        </TabsContent>

        <TabsContent value="renter" className="mt-6 space-y-6" data-testid="renter-panel">
          <DriverStreak />
          <ChampionBadge />
          <DriverCardPanel />
          <RoadReadyBadge />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={Clock} label="Upcoming" value={renter?.upcoming?.length ?? 0} accent="bg-blue-500/20 text-blue-400" />
            <Stat icon={Car} label="Active" value={renter?.active?.length ?? 0} accent="bg-cyan-500/20 text-cyan-400" />
            <Stat icon={TrendingUp} label="Completed" value={renter?.completed_count ?? 0} accent="bg-slate-800 text-slate-200" />
            <Stat icon={DollarSign} label="Total spent" value={`$${(renter?.total_spent ?? 0).toFixed(2)}`} accent="bg-emerald-500/20 text-emerald-400" />
          </div>

          <div>
            <h2 className="font-display text-xl font-bold mb-3">Upcoming trips</h2>
            {(renter?.upcoming || []).length === 0 ? (
              <div className="p-8 rounded-2xl border border-dashed border-slate-800 text-center">
                <p className="text-slate-400 mb-4">No upcoming trips. Find your next ride.</p>
                <Link to="/browse"><Button className="bg-white text-slate-950 hover:bg-slate-100 font-semibold" data-testid="renter-browse-btn">Browse cars <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              </div>
            ) : (
              <div className="space-y-2">
                {renter.upcoming.map((b) => (
                  <div key={b.booking_id} className="flex items-center justify-between p-4 rounded-xl bg-[#131722] border border-slate-800" data-testid={`renter-booking-${b.booking_id}`}>
                    <div className="flex items-center gap-4">
                      {b.car_photo && <img src={b.car_photo} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                      <div>
                        <p className="font-semibold">{b.car_label}</p>
                        <p className="text-xs text-slate-400">{b.start_date} → {b.end_date}</p>
                        <Badge className="mt-1 bg-slate-800 text-slate-200 text-[10px]">{b.status.replace(/_/g, " ")}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setChatBooking({ id: b.booking_id, label: b.car_label })} data-testid={`chat-btn-${b.booking_id}`} className="bg-transparent border-slate-700 hover:bg-slate-800">
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Chat
                      </Button>
                      <DisputeDialog bookingId={b.booking_id} onCreated={() => setDisputeKey((k) => k + 1)} trigger={
                        <Button variant="outline" size="sm" data-testid={`dispute-btn-${b.booking_id}`} className="bg-transparent border-slate-700 hover:bg-slate-800 text-amber-300">
                          <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />Dispute
                        </Button>} />
                      <p className="font-mono text-white">${b.total.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <MyDisputes refreshKey={disputeKey + 1000} />
          <SavedSearches />
          <Watchlist />
        </TabsContent>
      </Tabs>
      <div className="mt-10"><DeviceManager /></div>
      {chatBooking && (
        <BookingChat bookingId={chatBooking.id} label={chatBooking.label}
          open={!!chatBooking} onOpenChange={(v) => !v && setChatBooking(null)} />
      )}
    </main>
  );
}
